export const COMPETITOR_SLUGS = [
  'ourfamilywizard',
  'talkingparents',
  '2houses',
  'appclose',
  'coparently',
  'custody-x-change',
  'fayr',
  'weparent',
  'cozi',
  'supportpay',
] as const;

export type CompetitorSlug = (typeof COMPETITOR_SLUGS)[number];

export type CompetitorFeatureMatrix = {
  messaging: boolean;
  expenseTracking: boolean;
  custodyCalendar: boolean;
  documentStorage: boolean;
  reportExports: boolean;
  courtReadyRecords: boolean;
  iosAndroid: boolean;
  webAccess: boolean;
  freeTier: boolean;
};

export type CompetitorFaq = {
  question: string;
  answer: string;
};

export type CompetitorProfile = {
  slug: CompetitorSlug;
  name: string;
  tagline: string;
  pricingMonthly: string;
  pricingAnnual: string;
  pricingSourceUrl: string;
  bestFor: string;
  verdict: string;
  vsHarbor: string;
  pros: string[];
  cons: string[];
  featureMatrix: CompetitorFeatureMatrix;
  faqs: CompetitorFaq[];
};

export const COMPARISON_FEATURES: Array<{
  key: keyof CompetitorFeatureMatrix;
  label: string;
  harborIncluded: boolean;
}> = [
  { key: 'messaging', label: 'Secure messaging', harborIncluded: true },
  { key: 'expenseTracking', label: 'Expense tracking & split calculations', harborIncluded: true },
  { key: 'custodyCalendar', label: 'Custody calendar', harborIncluded: true },
  { key: 'documentStorage', label: 'Document/photo storage', harborIncluded: true },
  { key: 'reportExports', label: 'Report exports', harborIncluded: true },
  { key: 'courtReadyRecords', label: 'Court-ready records workflow', harborIncluded: true },
  { key: 'iosAndroid', label: 'iOS + Android apps', harborIncluded: true },
  { key: 'webAccess', label: 'Web app access', harborIncluded: true },
  { key: 'freeTier', label: 'Free tier available', harborIncluded: false },
];

export const COMPETITORS: CompetitorProfile[] = [
  {
    slug: 'ourfamilywizard',
    name: 'OurFamilyWizard',
    tagline: 'Established court-focused co-parenting platform.',
    pricingMonthly: 'From about $12.50 per parent/month (annual billing)',
    pricingAnnual: 'Typically around $300/year for two parents before add-ons',
    pricingSourceUrl: 'https://www.ourfamilywizard.com/pricing',
    bestFor: 'Families and attorneys who want a long-established platform used in many court orders.',
    verdict:
      'OurFamilyWizard is robust and familiar in legal settings, but Harbor is usually lower cost and simpler for day-to-day co-parent operations.',
    vsHarbor:
      'Both apps support messaging, expense tracking, and calendars. Harbor focuses on a simpler workflow with lower entry pricing, while OurFamilyWizard offers a deeper add-on ecosystem.',
    pros: [
      'Widely recognized in family-law workflows',
      'Strong documentation and records tooling',
      'Large support and education footprint',
    ],
    cons: [
      'Higher total cost for two parents',
      'Some features require additional paid tiers or add-ons',
      'Interface can feel heavier for quick daily use',
    ],
    featureMatrix: {
      messaging: true,
      expenseTracking: true,
      custodyCalendar: true,
      documentStorage: true,
      reportExports: true,
      courtReadyRecords: true,
      iosAndroid: true,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is Harbor cheaper than OurFamilyWizard?',
        answer:
          'In most common two-parent setups, Harbor is lower cost based on publicly listed pricing. Always verify current pricing on each vendor site before deciding.',
      },
      {
        question: 'Do both apps support court documentation?',
        answer:
          'Yes. Both platforms provide communication and expense records that can be shared with legal professionals.',
      },
      {
        question: 'Which is easier for everyday co-parent use?',
        answer:
          'Families who prioritize speed and simplicity often prefer Harbor, while families with court-specific process requirements may prefer OurFamilyWizard.',
      },
    ],
  },
  {
    slug: 'talkingparents',
    name: 'TalkingParents',
    tagline: 'Communication-first app built around documented messaging.',
    pricingMonthly: 'Plan options often start around $9.99 per parent/month',
    pricingAnnual: 'Annual total depends on tier and participants',
    pricingSourceUrl: 'https://talkingparents.com/pricing',
    bestFor: 'Families whose primary need is structured communication records.',
    verdict:
      'TalkingParents is strong for communication monitoring; Harbor provides a broader all-in-one co-parent stack with lower starting pricing.',
    vsHarbor:
      'Both support documented communication and custody coordination. Harbor places more emphasis on integrated expense workflows and simpler all-feature access.',
    pros: [
      'Well-known documented messaging model',
      'Includes web and mobile access',
      'Commonly referenced by family-law professionals',
    ],
    cons: [
      'Feature set varies by plan tier',
      'Total family cost can rise with paid plans',
      'Some users choose a second app for complete expense management',
    ],
    featureMatrix: {
      messaging: true,
      expenseTracking: true,
      custodyCalendar: true,
      documentStorage: true,
      reportExports: true,
      courtReadyRecords: true,
      iosAndroid: true,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is TalkingParents only for messaging?',
        answer:
          'Messaging is its core strength, but it also provides additional co-parenting tools. Exact features vary by tier.',
      },
      {
        question: 'How does Harbor differ from TalkingParents?',
        answer:
          'Harbor emphasizes one streamlined product experience with lower starting pricing and integrated expense + records workflows.',
      },
      {
        question: 'Can both apps be used for legal records?',
        answer:
          'Yes, both are used to maintain documented communication and shared records.',
      },
    ],
  },
  {
    slug: '2houses',
    name: '2houses',
    tagline: 'International co-parenting app with broad household features.',
    pricingMonthly: 'Commonly listed near $12.99/month per family',
    pricingAnnual: 'Often discounted annual family plan available',
    pricingSourceUrl: 'https://www.2houses.com/en/pricing',
    bestFor: 'Families wanting one shared account model and household coordination tools.',
    verdict:
      '2houses is a broad family organizer; Harbor is generally better for U.S.-style expense records and legal-ready workflows.',
    vsHarbor:
      '2houses offers many day-to-day co-parenting tools, while Harbor focuses heavily on clear documentation, exports, and straightforward shared-expense handling.',
    pros: [
      'Family-level pricing model',
      'Includes common coordination modules',
      'Available on web and mobile',
    ],
    cons: [
      'Pricing still higher than Harbor in many scenarios',
      'Court-document workflow expectations vary by jurisdiction',
      'U.S. legal-process focus may be lighter than Harbor’s positioning',
    ],
    featureMatrix: {
      messaging: true,
      expenseTracking: true,
      custodyCalendar: true,
      documentStorage: true,
      reportExports: true,
      courtReadyRecords: false,
      iosAndroid: true,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is 2houses a full co-parenting app?',
        answer:
          'Yes. It includes messaging, scheduling, and shared organization features for separated families.',
      },
      {
        question: 'When is Harbor a better fit than 2houses?',
        answer:
          'Harbor is often preferred when cost sensitivity, clean expense workflows, and court-oriented exports are top priorities.',
      },
      {
        question: 'Do both support web and mobile?',
        answer: 'Yes, both provide mobile apps and browser-based access.',
      },
    ],
  },
  {
    slug: 'appclose',
    name: 'AppClose',
    tagline: 'Popular low-cost entry option with core co-parenting tools.',
    pricingMonthly: 'Free core usage with optional paid services',
    pricingAnnual: 'No mandatory annual subscription for baseline use',
    pricingSourceUrl: 'https://appclose.com',
    bestFor: 'Families optimizing for minimal subscription cost.',
    verdict:
      'AppClose can be attractive for zero-cost adoption, while Harbor provides a more premium and consistent workflow for records and exports.',
    vsHarbor:
      'AppClose centers on free access, but Harbor offers a more structured end-to-end experience with predictable feature availability and pricing.',
    pros: [
      'No required base subscription in many cases',
      'Includes core co-parent communication and scheduling features',
      'Lower barrier to trial and adoption',
    ],
    cons: [
      'Feature depth and consistency can vary',
      'Legal-record workflows may require extra effort',
      'Some families outgrow the free-first model',
    ],
    featureMatrix: {
      messaging: true,
      expenseTracking: true,
      custodyCalendar: true,
      documentStorage: true,
      reportExports: false,
      courtReadyRecords: false,
      iosAndroid: true,
      webAccess: true,
      freeTier: true,
    },
    faqs: [
      {
        question: 'Is AppClose really free?',
        answer:
          'AppClose is known for free core features, with optional paid services depending on use case.',
      },
      {
        question: 'Why choose Harbor over a free app?',
        answer:
          'Families often choose Harbor for stronger consistency, lower conflict workflow design, and clearer record/export tooling.',
      },
      {
        question: 'Can I switch from AppClose to Harbor later?',
        answer:
          'Yes. Many families start with a free app and move to a paid platform when they need stronger structure and documentation.',
      },
    ],
  },
  {
    slug: 'coparently',
    name: 'Coparently',
    tagline: 'Legacy co-parenting app focused on plan and schedule management.',
    pricingMonthly: 'Historically listed around $9.99 per month',
    pricingAnnual: 'Annual pricing and availability can vary',
    pricingSourceUrl: 'https://www.coparently.com',
    bestFor: 'Families comparing long-running co-parenting app options.',
    verdict:
      'Coparently covers fundamentals, while Harbor offers a more modern product feel with tighter expense and records workflows.',
    vsHarbor:
      'Both target separated families coordinating schedules and communication. Harbor emphasizes current UX patterns and integrated exports.',
    pros: [
      'Longstanding brand in co-parenting software',
      'Covers core scheduling and communication tasks',
      'Simple conceptual model',
    ],
    cons: [
      'Product activity and updates may vary over time',
      'Feature parity with newer platforms can differ',
      'Pricing/value can be less compelling vs Harbor',
    ],
    featureMatrix: {
      messaging: true,
      expenseTracking: true,
      custodyCalendar: true,
      documentStorage: false,
      reportExports: true,
      courtReadyRecords: false,
      iosAndroid: true,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is Coparently still an option in 2026?',
        answer:
          'Some families still evaluate it, but buyers should verify current product status and pricing directly before choosing.',
      },
      {
        question: 'How is Harbor different from Coparently?',
        answer:
          'Harbor focuses on modern UX, predictable pricing, and a tightly integrated expense-to-export workflow.',
      },
      {
        question: 'Which app is better for records?',
        answer:
          'Families with documentation-heavy needs often prefer platforms with clearer export/report flows like Harbor.',
      },
    ],
  },
  {
    slug: 'custody-x-change',
    name: 'Custody X Change',
    tagline: 'Schedule and documentation software with legal-plan focus.',
    pricingMonthly: 'Often positioned as annual desktop/web planning software',
    pricingAnnual: 'Pricing varies by package and billing cadence',
    pricingSourceUrl: 'https://www.custodyxchange.com',
    bestFor: 'Parents who mainly need detailed custody schedule planning outputs.',
    verdict:
      'Custody X Change is strong for custody planning artifacts, while Harbor is stronger as a daily co-parent operations app.',
    vsHarbor:
      'Custody X Change focuses on planning and calendar artifacts; Harbor combines daily communication, expenses, and records in one ongoing system.',
    pros: [
      'Detailed custody calendar and planning tools',
      'Useful for generating formal parenting schedule outputs',
      'Known in custody-planning workflows',
    ],
    cons: [
      'Not always an all-in-one communication app substitute',
      'Pricing structure differs from typical monthly co-parent apps',
      'May require another tool for ongoing communication',
    ],
    featureMatrix: {
      messaging: false,
      expenseTracking: false,
      custodyCalendar: true,
      documentStorage: false,
      reportExports: true,
      courtReadyRecords: true,
      iosAndroid: false,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is Custody X Change a messaging app like Harbor?',
        answer:
          'Not primarily. It is generally known for planning and custody schedule outputs rather than day-to-day co-parent communication.',
      },
      {
        question: 'Should I use both Harbor and Custody X Change?',
        answer:
          'Some families use a planning tool plus an operations app. Harbor can be the daily system for messaging, expenses, and records.',
      },
      {
        question: 'Which is better for ongoing co-parenting logistics?',
        answer:
          'For daily communication and shared expense management, Harbor is typically the better fit.',
      },
    ],
  },
  {
    slug: 'fayr',
    name: 'Fayr',
    tagline: 'Co-parenting app with communication and scheduling support.',
    pricingMonthly: 'Public pricing may vary by region and plan',
    pricingAnnual: 'Annual options may be available depending on current offers',
    pricingSourceUrl: 'https://www.fayr.com',
    bestFor: 'Families evaluating newer or alternative co-parenting app brands.',
    verdict:
      'Fayr can cover core needs, while Harbor generally offers clearer all-in-one value for U.S. families focused on records and expenses.',
    vsHarbor:
      'Both target co-parent coordination; Harbor emphasizes transparent pricing and structured, export-friendly records.',
    pros: [
      'Modern co-parent app positioning',
      'Supports core communication/scheduling workflows',
      'Alternative choice for app comparison shoppers',
    ],
    cons: [
      'Public pricing details can be less straightforward',
      'Ecosystem and legal-workflow familiarity may vary by market',
      'Feature depth may differ from Harbor priorities',
    ],
    featureMatrix: {
      messaging: true,
      expenseTracking: true,
      custodyCalendar: true,
      documentStorage: true,
      reportExports: false,
      courtReadyRecords: false,
      iosAndroid: true,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is Fayr cheaper than Harbor?',
        answer:
          'Pricing can change and may vary by offer, so compare each vendor’s current plan page directly.',
      },
      {
        question: 'What does Harbor offer that Fayr shoppers look for?',
        answer:
          'Harbor is often selected for clear pricing, integrated expense tracking, and straightforward records exports.',
      },
      {
        question: 'Do both support mobile usage?',
        answer: 'Yes, both are designed for mobile-first co-parent workflows.',
      },
    ],
  },
  {
    slug: 'weparent',
    name: 'WeParent',
    tagline: 'Co-parenting planner with calendars, to-dos, and records.',
    pricingMonthly: 'Typically around $9.99/month range depending on plan',
    pricingAnnual: 'Often discounted annual billing options',
    pricingSourceUrl: 'https://weparent.app',
    bestFor: 'Families that want planner-style organization features.',
    verdict:
      'WeParent is useful for planning and organization, while Harbor usually wins on integrated expense + legal-record workflow clarity.',
    vsHarbor:
      'Both include calendar and communication capabilities. Harbor is often preferred when structured expense and export needs are central.',
    pros: [
      'Good planner-oriented experience',
      'Includes common family coordination modules',
      'Supports cross-platform use',
    ],
    cons: [
      'Can require extra effort for advanced legal-ready reporting',
      'Pricing can exceed Harbor for similar core outcomes',
      'Not always optimized for high-conflict documentation workflows',
    ],
    featureMatrix: {
      messaging: true,
      expenseTracking: true,
      custodyCalendar: true,
      documentStorage: true,
      reportExports: true,
      courtReadyRecords: false,
      iosAndroid: true,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is WeParent good for family organization?',
        answer:
          'Yes, WeParent is often evaluated for planner-style organization and scheduling features.',
      },
      {
        question: 'When do families switch from WeParent to Harbor?',
        answer:
          'Common reasons include wanting simpler pricing and stronger built-in workflows for expenses and evidence-ready records.',
      },
      {
        question: 'Can both apps handle custody calendars?',
        answer: 'Yes, both provide custody calendar functionality.',
      },
    ],
  },
  {
    slug: 'cozi',
    name: 'Cozi',
    tagline: 'General family organizer, not solely a co-parent app.',
    pricingMonthly: 'Free tier available; premium plan pricing varies',
    pricingAnnual: 'Annual premium subscription available',
    pricingSourceUrl: 'https://www.cozi.com',
    bestFor: 'Low-conflict family organization and shared household scheduling.',
    verdict:
      'Cozi is excellent for broad family organization, but Harbor is built specifically for co-parent communication, expenses, and records.',
    vsHarbor:
      'Cozi is a general family planner; Harbor is purpose-built for separated-parent workflows, including court-oriented documentation and expense reconciliation.',
    pros: [
      'Easy-to-adopt family organizer',
      'Free option lowers adoption friction',
      'Strong for shared lists and household logistics',
    ],
    cons: [
      'Not designed as a legal co-parenting records system',
      'Limited conflict-resolution/documentation context',
      'Expense and court reporting workflows are not core strengths',
    ],
    featureMatrix: {
      messaging: false,
      expenseTracking: false,
      custodyCalendar: true,
      documentStorage: false,
      reportExports: false,
      courtReadyRecords: false,
      iosAndroid: true,
      webAccess: true,
      freeTier: true,
    },
    faqs: [
      {
        question: 'Is Cozi a co-parenting app?',
        answer:
          'Cozi is primarily a general family organizer and not purpose-built for high-documentation co-parenting workflows.',
      },
      {
        question: 'Can I use Cozi for separated parenting?',
        answer:
          'You can for lightweight scheduling, but families needing documented messaging and expense evidence often use dedicated co-parent apps like Harbor.',
      },
      {
        question: 'Why compare Harbor to Cozi at all?',
        answer:
          'Many families start with a general organizer, then move to specialized co-parenting software as needs become more complex.',
      },
    ],
  },
  {
    slug: 'supportpay',
    name: 'SupportPay',
    tagline: 'Family expense management and reimbursement platform.',
    pricingMonthly: 'Pricing varies by plan and payer setup',
    pricingAnnual: 'Annual options may vary by contract type',
    pricingSourceUrl: 'https://www.supportpay.com',
    bestFor: 'Families primarily focused on reimbursement and support expense tracking.',
    verdict:
      'SupportPay is expense-centric, while Harbor covers expenses plus communication and calendar workflows in one co-parenting app.',
    vsHarbor:
      'SupportPay specializes in payment and reimbursement tracking. Harbor combines expense tracking with communication, scheduling, and records in a single product.',
    pros: [
      'Focused expense/reimbursement experience',
      'Useful for support and shared-cost tracking',
      'Recognized in family-finance workflows',
    ],
    cons: [
      'Not always a full substitute for messaging/calendar tools',
      'Families may need another app for daily co-parent coordination',
      'Pricing model can be less straightforward than Harbor',
    ],
    featureMatrix: {
      messaging: false,
      expenseTracking: true,
      custodyCalendar: false,
      documentStorage: true,
      reportExports: true,
      courtReadyRecords: false,
      iosAndroid: true,
      webAccess: true,
      freeTier: false,
    },
    faqs: [
      {
        question: 'Is SupportPay mainly for expenses?',
        answer:
          'Yes, SupportPay is generally positioned as an expense and reimbursement platform rather than a full co-parent communication system.',
      },
      {
        question: 'Can Harbor replace SupportPay?',
        answer:
          'For many families, Harbor can cover shared-expense tracking while also adding messaging, calendar, and records in the same app.',
      },
      {
        question: 'Should I choose SupportPay or Harbor?',
        answer:
          'Choose SupportPay if expense workflows are your only need; choose Harbor if you want one app for communication, scheduling, and expenses together.',
      },
    ],
  },
];

export function isCompetitorSlug(value: string): value is CompetitorSlug {
  return (COMPETITOR_SLUGS as readonly string[]).includes(value);
}

export function getCompetitorBySlug(slug: CompetitorSlug): CompetitorProfile {
  const competitor = COMPETITORS.find((item) => item.slug === slug);
  if (!competitor) {
    throw new Error(`Unknown competitor slug: ${slug}`);
  }
  return competitor;
}
